import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PointOfSale as PosIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Science as SandboxIcon,
  Verified as ProductionIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { settingsService, PosSisp } from '../../../services';
import { SISP_SERVICOS, i18nKeyServico } from '../../../constants/sispServicos';

type Environment = 'sandbox' | 'production';

/**
 * URLs SISP válidos por ambiente.
 *
 * O antigo default (pos.sisp.cv / mc.sisp.cv, porta 8443) foi descontinuado — os
 * hosts já nem resolvem em DNS. Quem criasse um terminal pela UI ficava com um URL
 * morto. Os valores abaixo são os que estão em uso e verificados.
 *
 * 3DS é obrigatório para compras (transactionCode=1) e suporta Visa/Mastercard
 * internacional; CardPayment serve recargas e pagamento de serviços (códigos 2 e 3).
 * A tokenização não usa estes URLs — tem os seus próprios (TokenEnrollment,
 * TokenPayment e cancel) em pg_sisp_tokenization.
 */
const URLS_SISP = {
  sandbox: {
    '3ds': 'https://mc.vinti4net.cv/3ds_middleware_php/public/3ds_init.php',
    cardPayment: 'https://mc.vinti4net.cv/BizMPIOnUsSisp/CardPayment',
  },
  production: {
    '3ds': 'https://3ds.vinti4net.cv/3ds/public/3ds_init.php',
    cardPayment: 'https://www.vinti4net.cv/bizmpi/CardPayment',
  },
} as const;

/** Chave estável para agrupar/indexar por serviço (catch-all = 'default') */
const chaveServico = (servico?: string | null) => servico || 'default';

const PosSispPage: React.FC = () => {
  const { t } = useTranslation();
  const [terminals, setTerminals] = useState<PosSisp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sispAmbiente, setSispAmbiente] = useState<'T' | 'P'>('T');
  const [switchingAmbiente, setSwitchingAmbiente] = useState(false);
  const [switchingServico, setSwitchingServico] = useState<string | null>(null);
  const [ambientePorServico, setAmbientePorServico] = useState<Record<string, 'T' | 'P' | null>>({});
  const [ambienteSuccess, setAmbienteSuccess] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [selected, setSelected] = useState<PosSisp | null>(null);
  const [activeTab, setActiveTab] = useState<Environment>('sandbox');
  const [formData, setFormData] = useState({
    designation: '',
    pos: '',
    posAuthCode: '',
    posUrl: '',
    servico: '',
    nib: '',
    iban: '',
    swift: '',
    banco: '',
    environment: 'sandbox' as Environment,
    status: 'S',
  });

  /** Nome legível de um serviço, para etiquetas e mensagens */
  const nomeServico = useCallback((servico?: string | null) => {
    if (!servico) return t('settings.posSisp.allServices');
    const key = i18nKeyServico(servico);
    return key ? `${servico} — ${t(key)}` : servico;
  }, [t]);

  const fetchTerminals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsService.getPosSisp();
      setTerminals(response.data || []);
    } catch (err: any) {
      console.error('Error fetching POS SISP:', err);
      setError(err.message || t('settings.posSisp.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchAmbiente = useCallback(async () => {
    try {
      const response = await settingsService.getSispAmbiente();
      if (response.success) {
        setSispAmbiente(response.data.ambiente);
      }
    } catch {
      // Default to T
    }
  }, []);

  const fetchAmbientePorServico = useCallback(async () => {
    try {
      const response = await settingsService.getSispAmbientePorServico();
      if (response.success) {
        const mapa: Record<string, 'T' | 'P' | null> = {};
        for (const linha of response.data) {
          mapa[chaveServico(linha.servico)] = linha.ambiente;
        }
        setAmbientePorServico(mapa);
      }
    } catch {
      // sem dados por serviço — os switches ficam no estado do terminal
    }
  }, []);

  useEffect(() => {
    fetchTerminals();
    fetchAmbiente();
    fetchAmbientePorServico();
  }, [fetchTerminals, fetchAmbiente, fetchAmbientePorServico]);

  const handleSwitchAmbiente = async () => {
    const newAmbiente = sispAmbiente === 'T' ? 'P' : 'T';
    const label = newAmbiente === 'T' ? t('settings.posSisp.sandboxTest') : t('settings.posSisp.production');

    if (newAmbiente === 'P' && !window.confirm(
      t('settings.posSisp.confirmSwitchProduction')
    )) {
      return;
    }

    setSwitchingAmbiente(true);
    setAmbienteSuccess(null);
    try {
      const response = await settingsService.setSispAmbiente(newAmbiente);
      if (response.success) {
        setSispAmbiente(newAmbiente);
        setAmbienteSuccess(t('settings.posSisp.environmentChanged', { label }));
        fetchTerminals();
        fetchAmbientePorServico();
      } else {
        setError(response.error || t('settings.posSisp.errorSwitching'));
      }
    } catch (err: any) {
      setError(err.error || err.message || t('settings.posSisp.errorSwitching'));
    } finally {
      setSwitchingAmbiente(false);
    }
  };

  const handleSwitchServico = async (servico: string | null) => {
    const chave = chaveServico(servico);
    const actual = ambientePorServico[chave] ?? 'T';
    const novo: 'T' | 'P' = actual === 'T' ? 'P' : 'T';
    const label = novo === 'T' ? t('settings.posSisp.sandboxTest') : t('settings.posSisp.production');

    if (novo === 'P' && !window.confirm(
      t('settings.posSisp.confirmSwitchProductionServico', { servico: nomeServico(servico) })
    )) {
      return;
    }

    setSwitchingServico(chave);
    setAmbienteSuccess(null);
    setError(null);
    try {
      const response = await settingsService.setSispAmbienteServico(servico, novo);
      if (response.success) {
        setAmbientePorServico({ ...ambientePorServico, [chave]: novo });
        setAmbienteSuccess(t('settings.posSisp.environmentChangedServico', { servico: nomeServico(servico), label }));
        fetchTerminals();
        fetchAmbiente();
      } else {
        setError(response.error || t('settings.posSisp.errorSwitchingServico'));
      }
    } catch (err: any) {
      setError(err.error || err.message || t('settings.posSisp.errorSwitchingServico'));
    } finally {
      setSwitchingServico(null);
    }
  };

  // Filter terminals by active tab
  const filteredTerminals = terminals.filter(t => {
    const env = t.environment?.toLowerCase() || 'production';
    return env === activeTab || (activeTab === 'production' && env === 'produção');
  });

  // Agrupar por serviço, com o catch-all sempre no fim
  const gruposServico: { servico: string | null; terminais: PosSisp[] }[] = (() => {
    const mapa = new Map<string, PosSisp[]>();
    for (const term of filteredTerminals) {
      const chave = chaveServico(term.servico);
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave)!.push(term);
    }
    return Array.from(mapa.entries())
      .map(([chave, terminais]) => ({ servico: chave === 'default' ? null : chave, terminais }))
      .sort((a, b) => {
        if (a.servico === null) return 1;
        if (b.servico === null) return -1;
        return a.servico.localeCompare(b.servico);
      });
  })();

  const sandboxCount = terminals.filter(t => t.environment?.toLowerCase() === 'sandbox').length;
  const productionCount = terminals.filter(t => {
    const env = t.environment?.toLowerCase() || 'production';
    return env === 'production' || env === 'produção';
  }).length;

  const getStatusChip = (status: string) => {
    if (status === 'S' || status === 'active') return <Chip size="small" color="success" label={t('common.active')} icon={<ActiveIcon />} />;
    return <Chip size="small" color="default" label={t('common.inactive')} icon={<InactiveIcon />} />;
  };

  const handleOpenEdit = (terminal?: PosSisp) => {
    if (terminal) {
      setSelected(terminal);
      const env = terminal.environment?.toLowerCase();
      setFormData({
        designation: terminal.designation,
        pos: terminal.pos,
        posAuthCode: terminal.posAuthCode,
        posUrl: terminal.posUrl,
        servico: terminal.servico || '',
        nib: terminal.nib || '',
        iban: terminal.iban || '',
        swift: terminal.swift || '',
        banco: terminal.banco || '',
        environment: (env === 'sandbox' ? 'sandbox' : 'production') as Environment,
        status: terminal.status,
      });
    } else {
      setSelected(null);
      setFormData({
        designation: '',
        pos: '',
        posAuthCode: '',
        posUrl: URLS_SISP[activeTab]['3ds'],
        servico: '',
        nib: '',
        iban: '',
        swift: '',
        banco: '',
        environment: activeTab,
        status: 'S',
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      // Map environment to backend format.
      // servico: '' significa catch-all — enviar null explícito para o backend
      // conseguir distinguir "limpar" de "não alterar".
      const dataToSave = {
        ...formData,
        servico: formData.servico || null,
        environment: formData.environment === 'sandbox' ? 'Teste' : 'Produção',
      };
      if (selected) {
        await settingsService.updatePosSisp(selected.id, dataToSave);
      } else {
        await settingsService.createPosSisp(dataToSave);
      }
      setEditDialog(false);
      setSelected(null);
      fetchTerminals();
      fetchAmbientePorServico();
    } catch (err: any) {
      // O backend devolve 409 com mensagem explícita em caso de POS duplicado
      setError(err.error || err.response?.data?.error || err.message || t('settings.posSisp.errorSaving'));
    }
  };

  const handleDelete = async (terminal: PosSisp) => {
    if (!window.confirm(t('settings.posSisp.confirmDelete', { name: terminal.designation }))) {
      return;
    }

    try {
      setError(null);
      await settingsService.deletePosSisp(terminal.id);
      fetchTerminals();
      fetchAmbientePorServico();
    } catch (err: any) {
      setError(err.error || err.response?.data?.error || err.message || t('settings.posSisp.errorDeleting'));
    }
  };

  if (loading && terminals.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {ambienteSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setAmbienteSuccess(null)}>
          {ambienteSuccess}
        </Alert>
      )}

      {/* Ambiente Switch */}
      <Card sx={{ mb: 3, border: sispAmbiente === 'P' ? '2px solid #ed6c02' : '2px solid #0288d1' }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {sispAmbiente === 'T' ? (
                <SandboxIcon sx={{ fontSize: 40, color: 'info.main' }} />
              ) : (
                <ProductionIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              )}
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {t('settings.posSisp.activeEnvironment')}: {sispAmbiente === 'T' ? t('settings.posSisp.sandboxLabel') : t('settings.posSisp.productionLabel')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sispAmbiente === 'T'
                    ? t('settings.posSisp.sandboxDescription')
                    : t('settings.posSisp.productionDescription')}
                </Typography>
                <Typography variant="caption" color="warning.main" fontWeight={600}>
                  {t('settings.posSisp.switchAllServices')}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              color={sispAmbiente === 'T' ? 'warning' : 'info'}
              startIcon={switchingAmbiente ? <CircularProgress size={16} /> : <SwapIcon />}
              onClick={handleSwitchAmbiente}
              disabled={switchingAmbiente}
            >
              {sispAmbiente === 'T' ? t('settings.posSisp.switchToProduction') : t('settings.posSisp.switchToSandbox')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('settings.posSisp.title')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          {t('settings.posSisp.newTerminal')}
        </Button>
      </Box>

      {/* Environment Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value="sandbox"
            icon={<SandboxIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('settings.posSisp.sandbox')}
                <Chip size="small" label={sandboxCount} color="info" />
              </Box>
            }
          />
          <Tab
            value="production"
            icon={<ProductionIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('settings.posSisp.production')}
                <Chip size="small" label={productionCount} color="success" />
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PosIcon color={activeTab === 'sandbox' ? 'info' : 'success'} />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{filteredTerminals.length}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeTab === 'sandbox' ? t('settings.posSisp.terminalsSandbox') : t('settings.posSisp.terminalsProduction')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {filteredTerminals.filter(t => t.status === 'S' || t.status === 'active').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.banks.activeMale')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Environment Info */}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.posSisp.designation')}</TableCell>
                <TableCell>POS ID</TableCell>
                <TableCell>POS Auth Code</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>{t('settings.posSisp.bank')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTerminals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('settings.posSisp.noTerminals', { env: activeTab === 'sandbox' ? t('settings.posSisp.sandbox') : t('settings.posSisp.production') })}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenEdit()}
                      sx={{ mt: 2 }}
                    >
                      {t('settings.posSisp.addTerminal')}
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                gruposServico.map((grupo) => {
                  const chave = chaveServico(grupo.servico);
                  const ambServico = ambientePorServico[chave] ?? null;
                  return (
                    <React.Fragment key={chave}>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell colSpan={5}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {nomeServico(grupo.servico)}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={2} align="right">
                          <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                              switchingServico === chave
                                ? <CircularProgress size={20} sx={{ mx: 1.5 }} />
                                : (
                                  <Switch
                                    size="small"
                                    color="warning"
                                    checked={ambServico === 'P'}
                                    onChange={() => handleSwitchServico(grupo.servico)}
                                  />
                                )
                            }
                            label={
                              <Typography variant="caption" fontWeight={600}>
                                {ambServico === 'P'
                                  ? t('settings.posSisp.production')
                                  : ambServico === 'T'
                                    ? t('settings.posSisp.sandbox')
                                    : t('settings.posSisp.noActivePos')}
                              </Typography>
                            }
                          />
                        </TableCell>
                      </TableRow>
                      {grupo.terminais.map((terminal) => (
                        <TableRow key={terminal.id} hover>
                          <TableCell sx={{ pl: 4 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {terminal.designation}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {terminal.pos}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              ••••••••
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                              {terminal.posUrl}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {terminal.banco || terminal.nib ? (
                              <>
                                <Typography variant="body2">{terminal.banco || '—'}</Typography>
                                {terminal.nib && (
                                  <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                                    NIB {terminal.nib}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>{getStatusChip(terminal.status)}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleOpenEdit(terminal)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(terminal)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {formData.environment === 'sandbox' ? (
            <SandboxIcon color="info" />
          ) : (
            <ProductionIcon color="success" />
          )}
          {selected ? t('settings.posSisp.editTerminal') : t('settings.posSisp.newTerminal')} - {formData.environment === 'sandbox' ? t('settings.posSisp.sandbox') : t('settings.posSisp.production')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.posSisp.designation')}
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                placeholder="Ex: Terminal Principal Sandbox"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label={t('settings.posSisp.servico')}
                value={formData.servico}
                onChange={(e) => setFormData({ ...formData, servico: e.target.value })}
                helperText={t('settings.posSisp.servicoHelp')}
              >
                <MenuItem value="">
                  <em>{t('settings.posSisp.allServices')}</em>
                </MenuItem>
                {SISP_SERVICOS.map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.code} — {t(s.i18n)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="POS ID"
                value={formData.pos}
                onChange={(e) => setFormData({ ...formData, pos: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                placeholder="Ex: 90041"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="POS Auth Code"
                value={formData.posAuthCode}
                onChange={(e) => setFormData({ ...formData, posAuthCode: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                type="password"
                placeholder={t('settings.posSisp.authCodePlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.posSisp.posUrl')}
                value={formData.posUrl}
                onChange={(e) => setFormData({ ...formData, posUrl: e.target.value })}
                placeholder="https://"
                helperText={
                  `3DS (compras): ${URLS_SISP[formData.environment]['3ds']}`
                  + ` · CardPayment (recargas/serviços): ${URLS_SISP[formData.environment].cardPayment}`
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>
                {t('settings.posSisp.bankDetails')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('settings.posSisp.bankDetailsHelp')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.posSisp.bank')}
                value={formData.banco}
                onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                placeholder="Ex: BCN"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="NIB"
                value={formData.nib}
                onChange={(e) => setFormData({ ...formData, nib: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="IBAN"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                placeholder="CV..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="SWIFT"
                value={formData.swift}
                onChange={(e) => setFormData({ ...formData, swift: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('settings.posSisp.environment')}
                value={formData.environment}
                onChange={(e) => {
                  const newEnv = e.target.value as Environment;
                  setFormData({
                    ...formData,
                    environment: newEnv,
                    posUrl: URLS_SISP[newEnv]['3ds'],
                  });
                }}
              >
                <MenuItem value="sandbox">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SandboxIcon fontSize="small" color="info" />
                    {t('settings.posSisp.sandboxTests')}
                  </Box>
                </MenuItem>
                <MenuItem value="production">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ProductionIcon fontSize="small" color="success" />
                    {t('settings.posSisp.production')}
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="S">{t('common.active')}</MenuItem>
                <MenuItem value="N">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formData.designation || !formData.pos}>
            {selected ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PosSispPage;
